import type { AddressCandidate } from "./addressCandidate";
import {
    buildResolvedAddressFields,
    extractDetailLinesFromFormattedAddress,
    findRouteComponent,
    isDuplicateAddressSegment,
    isEffectivelySameAddressLine,
    isExactAddressMatch,
    normalizeAddressLineLayout,
    normalizeAddressText,
    parseGoogleAddressComponents,
    resolveIntermediateLocalityFromComponents,
    resolveSublocalityLine,
    resolveTown,
    streetLineIncludesRoute,
    type GooglePlaceAddressFields,
    type ParsedGoogleAddressComponents,
} from "./googleAddressParts";

export interface AddressMappingResult {
    address: GooglePlaceAddressFields;
    suggestedLocationName: string | null;
}

type AddressIdentityClass = "commercialPoi" | "buildingWithStreet" | "streetAddress";

interface AddressLines {
    addressLine1: string;
    addressLine2: string;
    addressLine3: string;
}

function classifyIdentity(
    displayName: string | null,
    parsed: ParsedGoogleAddressComponents
): AddressIdentityClass {
    const name = displayName?.trim() ?? "";
    if (!name) return "streetAddress";

    if (
        parsed.premise &&
        isEffectivelySameAddressLine(name, parsed.premise) &&
        parsed.route &&
        !isEffectivelySameAddressLine(name, parsed.route)
    ) {
        return "buildingWithStreet";
    }

    if (
        parsed.route &&
        isEffectivelySameAddressLine(name, parsed.premise || parsed.fullStreetLine) &&
        !isEffectivelySameAddressLine(name, parsed.route)
    ) {
        return "buildingWithStreet";
    }

    if (
        parsed.route &&
        parsed.streetNumber &&
        parsed.fullStreetLine &&
        !isEffectivelySameAddressLine(name, parsed.fullStreetLine) &&
        normalizeAddressText(name).includes(normalizeAddressText(parsed.streetNumber))
    ) {
        return "buildingWithStreet";
    }

    if (parsed.fullStreetLine && isEffectivelySameAddressLine(name, parsed.fullStreetLine)) {
        return "streetAddress";
    }

    if (
        parsed.fullStreetLine &&
        !isEffectivelySameAddressLine(name, parsed.fullStreetLine) &&
        !streetLineIncludesRoute(name, parsed.route)
    ) {
        return "commercialPoi";
    }

    if (parsed.fullStreetLine && streetLineIncludesRoute(parsed.fullStreetLine, name)) {
        return "streetAddress";
    }

    return "commercialPoi";
}

function assignStructuredLines(
    identity: AddressIdentityClass,
    displayName: string | null,
    parsed: ParsedGoogleAddressComponents,
    components: AddressCandidate["addressComponents"],
    town: string
): AddressLines {
    const sublocality = resolveSublocalityLine(parsed, town);
    const intermediateLocality = resolveIntermediateLocalityFromComponents(components, town, displayName ?? "");
    const routeName = findRouteComponent(components);

    if (identity === "buildingWithStreet") {
        const line1 = (displayName?.trim() ?? parsed.premise) || parsed.fullStreetLine;
        const line2 = parsed.route || parsed.routeAreaLine;
        const line3 = sublocality || parsed.subpremise;
        return { addressLine1: line1, addressLine2: line2, addressLine3: line3 };
    }

    if (identity === "commercialPoi") {
        const line1 = displayName?.trim() ?? "";

        if (parsed.subpremise && (parsed.fullStreetLine || parsed.routeAreaLine || routeName)) {
            const siteLine = parsed.routeAreaLine || routeName || parsed.fullStreetLine;
            return {
                addressLine1: line1,
                addressLine2: parsed.subpremise,
                addressLine3: siteLine && !isExactAddressMatch(parsed.subpremise, siteLine) ? siteLine : sublocality,
            };
        }

        if (parsed.premise && parsed.routeAreaLine && !parsed.streetNumber) {
            return {
                addressLine1: line1,
                addressLine2: parsed.premise,
                addressLine3: parsed.routeAreaLine,
            };
        }

        if (parsed.fullStreetLine && !isEffectivelySameAddressLine(line1, parsed.fullStreetLine)) {
            return {
                addressLine1: line1,
                addressLine2: parsed.fullStreetLine,
                addressLine3: sublocality || intermediateLocality,
            };
        }

        if (!parsed.fullStreetLine && intermediateLocality) {
            return {
                addressLine1: line1,
                addressLine2: intermediateLocality,
                addressLine3: sublocality,
            };
        }

        return {
            addressLine1: line1,
            addressLine2: parsed.fullStreetLine || parsed.subpremise || intermediateLocality,
            addressLine3: sublocality || parsed.routeAreaLine,
        };
    }

    // streetAddress
    let line1 = parsed.fullStreetLine || parsed.premise || parsed.route;
    let line2 = parsed.subpremise;
    let line3 = "";

    if (parsed.routeAreaLine) {
        if (line2) {
            line3 = parsed.routeAreaLine;
        } else {
            line2 = parsed.routeAreaLine;
        }
    }

    if (!line2 && sublocality) {
        line2 = sublocality;
    } else if (!line3 && sublocality && !isExactAddressMatch(line2, sublocality)) {
        line3 = sublocality;
    }

    return { addressLine1: line1, addressLine2: line2, addressLine3: line3 };
}

function canUseFormattedSegment(segment: string, line1: string, line2: string): boolean {
    if (!segment.trim()) return false;
    if (isDuplicateAddressSegment(segment, line1)) return false;
    if (line2.trim() && isDuplicateAddressSegment(segment, line2)) return false;
    return true;
}

function applyFormattedFallback(
    lines: AddressLines,
    candidate: AddressCandidate,
    town: string,
    parsed: ParsedGoogleAddressComponents
): AddressLines {
    const formattedText = candidate.formattedAddress ?? candidate.predictionDescription ?? "";
    if (!formattedText.trim()) return lines;

    const displayName = candidate.displayName ?? lines.addressLine1;
    const detailLines = extractDetailLinesFromFormattedAddress(formattedText, {
        displayName,
        town,
        postcode: parsed.postcode,
        country: parsed.country || "United Kingdom",
    });

    if (detailLines.length === 0) return lines;

    let { addressLine1, addressLine2, addressLine3 } = lines;
    const [first, second] = detailLines;

    if (!addressLine2.trim() && !addressLine3.trim()) {
        if (!addressLine1.trim() && first) {
            addressLine1 = first;
            if (second && canUseFormattedSegment(second, addressLine1, "")) {
                addressLine2 = second;
            }
        } else {
            if (first && canUseFormattedSegment(first, addressLine1, addressLine2)) {
                addressLine2 = first;
            }
            if (second && canUseFormattedSegment(second, addressLine1, addressLine2)) {
                addressLine3 = second;
            }
        }
        return { addressLine1, addressLine2, addressLine3 };
    }

    if (
        first &&
        isExactAddressMatch(addressLine2, displayName) &&
        !isExactAddressMatch(first, displayName) &&
        canUseFormattedSegment(first, addressLine1, "")
    ) {
        addressLine2 = first;
        if (second && canUseFormattedSegment(second, addressLine1, addressLine2)) {
            addressLine3 = second;
        }
    } else if (
        first &&
        streetLineIncludesRoute(addressLine2, first) &&
        !isExactAddressMatch(addressLine2, first) &&
        canUseFormattedSegment(first, addressLine1, addressLine2)
    ) {
        addressLine2 = first;
    } else {
        if (!addressLine2.trim() && first && canUseFormattedSegment(first, addressLine1, "")) {
            addressLine2 = first;
        }
        if (!addressLine3.trim() && second && canUseFormattedSegment(second, addressLine1, addressLine2)) {
            addressLine3 = second;
        } else if (
            !addressLine3.trim() &&
            first &&
            canUseFormattedSegment(first, addressLine1, addressLine2) &&
            !streetLineIncludesRoute(addressLine2, first)
        ) {
            addressLine3 = first;
        }
    }

    return { addressLine1, addressLine2, addressLine3 };
}

export function mapAddressCandidateToFormFields(candidate: AddressCandidate): AddressMappingResult {
    const parsed = parseGoogleAddressComponents(candidate.addressComponents);
    const town = resolveTown(parsed);
    const identity = classifyIdentity(candidate.displayName, parsed);

    let lines = assignStructuredLines(
        identity,
        candidate.displayName,
        parsed,
        candidate.addressComponents,
        town
    );

    lines = applyFormattedFallback(lines, candidate, town, parsed);
    lines = normalizeAddressLineLayout(lines, {
        town,
        county: parsed.county,
        postcode: parsed.postcode,
    });

    const suggestedLocationName =
        identity === "commercialPoi" ? candidate.displayName?.trim() || null : null;

    return {
        address: buildResolvedAddressFields(lines, parsed, town),
        suggestedLocationName,
    };
}
